<template>
    <div ref="carouselWrapper" class="carousel-wrapper">
        <a-button
            type="text"
            class="fullscreen-btn"
            @click="toggleFullscreen"
            :aria-label="isFullscreen ? 'Sair da tela cheia' : 'Entrar em tela cheia'"
        >
            <component :is="isFullscreen ? FullscreenExitOutlined : FullscreenOutlined"/>
        </a-button>

        <a-carousel
            dot-position="bottom"
            :autoplay="hasMultipleFiles"
            :infinite="hasMultipleFiles"
            :dots="hasMultipleFiles"
            :autoplaySpeed="2000"
            class="carousel-container"
        >
            <div
                v-for="(file, index) in files"
                :key="index"
                class="carousel-slide"
            >
                <template v-if="file.typeFile === 'image'">
                    <img
                        :src="getFileSrc(file)"
                        alt="Imagem"
                        class="carousel-image"
                    />
                </template>
                <template v-else-if="file.typeFile === 'pdf'">
                    <embed
                        :src="getFileSrc(file)"
                        type="application/pdf"
                        class="carousel-pdf"
                    />
                </template>
                <template v-else>
                    <div class="unsupported-file">
                        Arquivo não suportado.
                    </div>
                </template>
            </div>
        </a-carousel>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount} from 'vue'
import {FullscreenOutlined, FullscreenExitOutlined} from '@ant-design/icons-vue'

const props = defineProps(['files'])
const route = "/proxy/estorage?id="

const isFullscreen = ref(false)
const carouselWrapper = ref(null)

const files = computed(() => props.files ?? [])
const hasMultipleFiles = computed(() => files.value.length > 1)

const SLIDE_RATIO = 2 / 3

const WIDTH_TOLERANCE = 24

let appliedWidth = 0
let resizeObserver = null

function updateSlideHeight() {
    const el = carouselWrapper.value

    if (!el || document.fullscreenElement) {
        return
    }

    const width = el.clientWidth

    if (!width || Math.abs(width - appliedWidth) < WIDTH_TOLERANCE) {
        return
    }

    appliedWidth = width
    el.style.setProperty('--slide-height', `${Math.round(width * SLIDE_RATIO)}px`)
}

const getFileSrc = (file) => file.url || (route + file.estorageId)

function toggleFullscreen() {
    const el = carouselWrapper.value

    if (!document.fullscreenElement) {
        el.requestFullscreen().catch(err => {
            console.error("Erro ao entrar em tela cheia:", err)
        })
    } else {
        document.exitFullscreen()
    }
}

function handleFullscreenChange() {
    isFullscreen.value = !!document.fullscreenElement
}

onMounted(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange)

    updateSlideHeight()

    if (typeof ResizeObserver !== 'undefined' && carouselWrapper.value) {
        resizeObserver = new ResizeObserver(updateSlideHeight)
        resizeObserver.observe(carouselWrapper.value)
    }
})

onBeforeUnmount(() => {
    document.removeEventListener('fullscreenchange', handleFullscreenChange)

    resizeObserver?.disconnect()
    resizeObserver = null
})
</script>

<style scoped>
.carousel-wrapper {
    position: relative;
    width: 100%;
    max-width: 100%;
    padding-bottom: 40px; /* Espaço para os dots fora */
    overflow: visible;
    background-color: dimgrey;
}

.carousel-container {
    overflow: visible !important;
    width: 100%;
}

.carousel-slide {
    display: flex !important;
    justify-content: center;
    align-items: center;
    background-color: dimgrey;
    aspect-ratio: 3 / 2;
    height: var(--slide-height, auto);
    width: 100%;
    overflow: hidden;
    position: relative;
}

.carousel-image,
.carousel-pdf {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
    background-color: transparent;
}

.carousel-pdf {
    width: 100%;
    height: 100%;
    border: none;
}

.unsupported-file {
    text-align: center;
    color: white;
    padding: 40px;
    width: 100%;
}

/* Botão fullscreen */
.fullscreen-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    border-radius: 4px;
    padding: 6px;
    cursor: pointer;
    color: white;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.fullscreen-btn:hover {
    background: rgba(0, 0, 0, 0.8);
}

.fullscreen-btn svg {
    font-size: 20px;
}

.carousel-wrapper:fullscreen,
.carousel-wrapper:-webkit-full-screen {
    padding-bottom: 0 !important;
}

.carousel-wrapper:fullscreen .carousel-container,
.carousel-wrapper:-webkit-full-screen .carousel-container {
    height: 100vh;
}

.carousel-wrapper:fullscreen .carousel-slide,
.carousel-wrapper:-webkit-full-screen .carousel-slide {
    height: 100vh;
    aspect-ratio: auto;
}

::v-deep(.slick-dots-bottom) {
    position: absolute;
    bottom: -30px; /* Fora do container no modo normal */
    width: 100%;
    display: flex !important;
    justify-content: center;
    z-index: 1000;
}

.carousel-wrapper:fullscreen ::v-deep(.slick-dots-bottom),
.carousel-wrapper:-webkit-full-screen ::v-deep(.slick-dots-bottom) {
    bottom: 20px !important; /* Visível na tela cheia */
}

</style>
